class TootoProtectorFeatures extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-tooto-protector-track]');
    this.cards = Array.from(this.querySelectorAll('[data-tooto-protector-card]'));
    this.carouselQuery = window.matchMedia('(max-width: 1024px)');
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.transitionSequence = 0;
    this.autoplayEnabled = this.dataset.autoplay === 'true' && this.cards.length > 1;
    this.autoplayDelay = Math.max(3000, Number.parseInt(this.dataset.autoplayDelay, 10) || 4000);
    this.autoplayVisible = false;

    this.cards.forEach((card) => {
      card.querySelector('[data-tooto-protector-toggle]')?.addEventListener('click', () => {
        this.setActive(card);
        this.restartAutoplay();
      });
    });

    this.handleResize = () => {
      this.measureCardSizes();
      this.measureTrackHeight();
      this.centerActive('auto');
    };
    window.addEventListener('resize', this.handleResize, { passive: true });
    this.measureCardSizes();
    this.measureTrackHeight();
    requestAnimationFrame(() => this.centerActive('auto'));
    document.fonts?.ready.then(() => {
      this.measureCardSizes();
      this.measureTrackHeight();
    });
    this.initializeAutoplay();
    this.initializeGrainient();
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('visibilitychange', this.handleAutoplayVisibilityChange);
    this.track?.removeEventListener('pointerenter', this.handleAutoplayPointerEnter);
    this.track?.removeEventListener('pointerleave', this.handleAutoplayPointerLeave);
    this.removeEventListener('focusin', this.handleAutoplayFocusIn);
    this.removeEventListener('focusout', this.handleAutoplayFocusOut);
    this.reducedMotionQuery?.removeEventListener?.('change', this.handleReducedMotionChange);
    this.autoplayObserver?.disconnect();
    this.clearAutoplay();
    if (this.autoplayFocusFrame) cancelAnimationFrame(this.autoplayFocusFrame);
    this.cancelContentReveal?.();
    this.grainientResizeObserver?.disconnect();
    this.grainientObserver?.disconnect();
    if (this.grainientFrame) cancelAnimationFrame(this.grainientFrame);
  }

  initializeAutoplay() {
    if (!this.autoplayEnabled) return;

    this.handleAutoplayPointerEnter = () => this.clearAutoplay();
    this.handleAutoplayPointerLeave = () => this.scheduleAutoplay();
    this.handleAutoplayFocusIn = () => this.clearAutoplay();
    this.handleAutoplayFocusOut = () => {
      if (this.autoplayFocusFrame) cancelAnimationFrame(this.autoplayFocusFrame);
      this.autoplayFocusFrame = requestAnimationFrame(() => {
        if (!this.matches(':focus-within')) this.scheduleAutoplay();
      });
    };
    this.handleAutoplayVisibilityChange = () => {
      if (document.hidden) this.clearAutoplay();
      else this.scheduleAutoplay();
    };
    this.handleReducedMotionChange = () => {
      if (this.reducedMotionQuery.matches) this.clearAutoplay();
      else this.scheduleAutoplay();
    };

    this.track?.addEventListener('pointerenter', this.handleAutoplayPointerEnter);
    this.track?.addEventListener('pointerleave', this.handleAutoplayPointerLeave);
    this.addEventListener('focusin', this.handleAutoplayFocusIn);
    this.addEventListener('focusout', this.handleAutoplayFocusOut);
    document.addEventListener('visibilitychange', this.handleAutoplayVisibilityChange);
    this.reducedMotionQuery.addEventListener?.('change', this.handleReducedMotionChange);

    this.autoplayObserver = new IntersectionObserver(
      ([entry]) => {
        this.autoplayVisible = entry?.isIntersecting ?? false;
        if (this.autoplayVisible) this.scheduleAutoplay();
        else this.clearAutoplay();
      },
      { threshold: 0.35 }
    );
    this.autoplayObserver.observe(this);
  }

  clearAutoplay() {
    if (!this.autoplayTimer) return;
    window.clearTimeout(this.autoplayTimer);
    this.autoplayTimer = null;
  }

  restartAutoplay() {
    this.clearAutoplay();
    this.scheduleAutoplay();
  }

  scheduleAutoplay() {
    this.clearAutoplay();
    if (
      !this.autoplayEnabled ||
      !this.autoplayVisible ||
      document.hidden ||
      this.reducedMotionQuery.matches ||
      this.track?.matches(':hover') ||
      this.matches(':focus-within')
    ) return;

    this.autoplayTimer = window.setTimeout(() => {
      this.autoplayTimer = null;
      const activeIndex = this.cards.findIndex((card) => card.classList.contains('is-active'));
      const nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % this.cards.length;
      this.setActive(this.cards[nextIndex]);
      this.scheduleAutoplay();
    }, this.autoplayDelay);
  }

  setActive(activeCard) {
    if (!activeCard || activeCard.classList.contains('is-active')) return;

    const sequence = ++this.transitionSequence;
    this.cancelContentReveal?.();
    this.classList.add('is-switching');

    const previousCard = this.querySelector('.is-active');
    previousCard?.classList.add('is-collapsing');

    this.cards.forEach((card) => {
      const isActive = card === activeCard;
      card.classList.remove('is-content-visible');
      card.classList.toggle('is-active', isActive);
      card.querySelector('[data-tooto-protector-toggle]')?.setAttribute('aria-expanded', String(isActive));
      card.querySelector('.tooto-protector-features__copy')?.setAttribute('aria-hidden', 'true');
    });

    requestAnimationFrame(() => {
      this.centerActive();
      this.scheduleContentReveal(activeCard, sequence);
    });
  }

  scheduleContentReveal(card, sequence) {
    const copy = card.querySelector('.tooto-protector-features__copy');

    const reveal = () => {
      if (sequence !== this.transitionSequence || !card.classList.contains('is-active')) return;

      this.cancelContentReveal?.();
      this.cards.forEach((item) => item.classList.remove('is-collapsing'));
      card.classList.add('is-content-visible');
      copy?.setAttribute('aria-hidden', 'false');
      this.classList.remove('is-switching');
    };

    if (this.reducedMotionQuery.matches) {
      reveal();
      return;
    }

    const handleTransitionEnd = (event) => {
      if (event.propertyName !== 'width' && event.propertyName !== 'height') return;
      reveal();
    };

    card.addEventListener('transitionend', handleTransitionEnd);
    const timeout = window.setTimeout(reveal, 120);

    this.cancelContentReveal = () => {
      card.removeEventListener('transitionend', handleTransitionEnd);
      window.clearTimeout(timeout);
      this.cancelContentReveal = null;
    };
  }

  measureCardSizes() {
    this.classList.add('is-measuring');
    let tallestOpenCard = 0;

    this.cards.forEach((card) => {
      const toggle = card.querySelector('[data-tooto-protector-toggle]');
      const copy = card.querySelector('.tooto-protector-features__copy');
      if (!toggle) return;

      const wasActive = card.classList.contains('is-active');
      const wasVisible = card.classList.contains('is-content-visible');
      card.classList.remove('is-active', 'is-content-visible', 'is-collapsing');
      card.style.removeProperty('--tooto-protector-open-width');
      card.style.width = 'max-content';
      card.style.height = 'auto';
      if (copy) copy.style.display = 'none';
      card.classList.remove('is-active');
      toggle.style.width = 'max-content';
      const closedRect = card.getBoundingClientRect();

      card.style.setProperty('--tooto-protector-closed-width', `${Math.ceil(closedRect.width)}px`);
      card.style.setProperty('--tooto-protector-closed-height', `${Math.ceil(closedRect.height)}px`);

      if (copy) copy.style.removeProperty('display');
      toggle.style.width = '';
      card.style.removeProperty('width');
      card.classList.add('is-active', 'is-content-visible');
      const openWidth = Math.ceil(card.getBoundingClientRect().width);
      card.style.setProperty('--tooto-protector-open-width', `${openWidth}px`);
      card.style.height = 'auto';
      const openHeight = Math.ceil(card.getBoundingClientRect().height);
      tallestOpenCard = Math.max(tallestOpenCard, openHeight);

      card.style.setProperty('--tooto-protector-open-height', `${openHeight}px`);
      card.classList.toggle('is-active', wasActive);
      card.classList.toggle('is-content-visible', wasVisible);
      card.style.removeProperty('height');
    });

    this.mobileOpenHeight = this.mobileQuery.matches ? Math.max(124, tallestOpenCard) : 0;

    this.classList.remove('is-measuring');
  }

  measureTrackHeight() {
    if (!this.track) return;

    if (!this.carouselQuery.matches) {
      this.track.style.removeProperty('--tooto-protector-track-height');
      return;
    }

    const trackHeight = this.mobileQuery.matches && this.mobileOpenHeight
      ? this.mobileOpenHeight + 8
      : 124;
    this.track.style.setProperty('--tooto-protector-track-height', `${trackHeight}px`);
  }

  centerActive(behavior = 'smooth') {
    if (!this.carouselQuery.matches || !this.track) return;
    const activeCard = this.querySelector('.is-active');
    if (!activeCard) return;

    this.track.scrollTo({
      left: activeCard.offsetLeft - (this.track.clientWidth - activeCard.offsetWidth) / 2,
      behavior
    });
  }

  async initializeGrainient() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const background = this.closest('.shopify-section')?.querySelector('[data-tooto-protector-background]');
    if (!background) return;

    try {
      const { Mesh, Program, Renderer, Triangle } = await import('https://cdn.jsdelivr.net/npm/ogl/+esm');
      if (!this.isConnected) return;

      const renderer = new Renderer({ webgl: 2, alpha: true, antialias: false, dpr: Math.min(devicePixelRatio || 1, 2) });
      const gl = renderer.gl;
      gl.canvas.setAttribute('aria-hidden', 'true');
      background.replaceChildren(gl.canvas);

      const parseHexColor = (value, fallback) => {
        const hex = value.trim().replace('#', '');
        if (!/^[\da-f]{6}$/i.test(hex)) return new Float32Array(fallback);

        return new Float32Array([
          Number.parseInt(hex.slice(0, 2), 16) / 255,
          Number.parseInt(hex.slice(2, 4), 16) / 255,
          Number.parseInt(hex.slice(4, 6), 16) / 255
        ]);
      };
      const backgroundStyles = getComputedStyle(background);
      const gradientStart = parseHexColor(
        backgroundStyles.getPropertyValue('--tooto-protector-gradient-start'),
        [.192, .039, .322]
      );
      const gradientEnd = parseHexColor(
        backgroundStyles.getPropertyValue('--tooto-protector-gradient-end'),
        [.651, .227, .086]
      );

      const vertex = `#version 300 es
        in vec2 position;
        void main(){ gl_Position=vec4(position,0.0,1.0); }
      `;
      const fragment = `#version 300 es
        precision highp float;
        uniform vec2 iResolution; uniform float iTime;
        uniform vec3 uGradientStart; uniform vec3 uGradientEnd;
        out vec4 fragColor;
        #define S(a,b,t) smoothstep(a,b,t)
        mat2 Rot(float a){float s=sin(a),c=cos(a);return mat2(c,-s,s,c);}
        vec2 hash(vec2 p){p=vec2(dot(p,vec2(2127.1,81.17)),dot(p,vec2(1269.5,283.37)));return fract(sin(p)*43758.5453);}
        float noise(vec2 p){vec2 i=floor(p),f=fract(p),u=f*f*(3.0-2.0*f);float n=mix(mix(dot(-1.0+2.0*hash(i),f),dot(-1.0+2.0*hash(i+vec2(1.,0.)),f-vec2(1.,0.)),u.x),mix(dot(-1.0+2.0*hash(i+vec2(0.,1.)),f-vec2(0.,1.)),dot(-1.0+2.0*hash(i+vec2(1.,1.)),f-vec2(1.,1.)),u.x),u.y);return .5+.5*n;}
        void main(){
          float t=iTime*.55; vec2 uv=gl_FragCoord.xy/iResolution.xy; float ratio=iResolution.x/iResolution.y;
          vec2 p=(uv-.5)/.9; float degree=noise(vec2(t*.1,p.x*p.y)*2.);
          p.y/=ratio; p*=Rot(radians((degree-.5)*650.+180.)); p.y*=ratio;
          float amplitude=32./1.8, warpTime=t*2.8;
          p.x+=sin(p.y*5.+warpTime)/amplitude; p.y+=sin(p.x*7.5+warpTime)/(amplitude*.5);
          float colorCycle=.5+.5*sin(t*.9);
          float blendX=p.x+sin(t*.72)*.16, softness=.12, balance=-.12+cos(t*.58)*.08;
          vec3 dynamicEnd=mix(uGradientEnd,uGradientStart,.22*colorCycle);
          vec3 dynamicStart=mix(uGradientStart,uGradientEnd,.22*(1.-colorCycle));
          vec3 c1=vec3(.01,.006,.018), c2=mix(c1,dynamicEnd,.84), c3=mix(c1,dynamicStart,.84);
          vec3 layer1=mix(c3,c2,S(-.3-balance-softness,.2-balance+softness,blendX));
          vec3 layer2=mix(c2,c1,S(-.3-balance-softness,.2-balance+softness,blendX));
          vec3 col=mix(layer1,layer2,S(.5-balance+softness,-.3-balance-softness,p.y));
          vec2 colorCenter=vec2(.5+sin(t*.48)*.07,.5+cos(t*.4)*.05);
          float colorField=1.-S(.18,.66,length((uv-colorCenter)*vec2(1.,.86)));
          col=mix(c1,col,.18+.82*colorField);
          float grain=fract(sin(dot(uv*2.+vec2(iTime*.05),vec2(12.9898,78.233)))*43758.5453);
          col+=(grain-.5)*.07; col+=.012; col=(col-.5)*1.08+.5;
          float luma=dot(col,vec3(.2126,.7152,.0722)); col=mix(vec3(luma),col,1.);
          col=pow(max(col,0.),vec3(1./1.05)); fragColor=vec4(clamp(col,0.,1.),1.);
        }
      `;
      const program = new Program(gl, {
        vertex,
        fragment,
        uniforms: {
          iTime: { value: 0 },
          iResolution: { value: new Float32Array([1, 1]) },
          uGradientStart: { value: gradientStart },
          uGradientEnd: { value: gradientEnd }
        }
      });
      const mesh = new Mesh(gl, { geometry: new Triangle(gl), program });
      const resize = () => {
        const rect = background.getBoundingClientRect();
        renderer.setSize(Math.max(1, rect.width), Math.max(1, rect.height));
        program.uniforms.iResolution.value[0] = gl.drawingBufferWidth;
        program.uniforms.iResolution.value[1] = gl.drawingBufferHeight;
        renderer.render({ scene: mesh });
      };
      this.grainientResizeObserver = new ResizeObserver(resize);
      this.grainientResizeObserver.observe(background);
      resize();

      let visible = true;
      const started = performance.now();
      const draw = (now) => {
        if (visible && !document.hidden) {
          program.uniforms.iTime.value = (now - started) * .001;
          renderer.render({ scene: mesh });
        }
        this.grainientFrame = requestAnimationFrame(draw);
      };
      this.grainientObserver = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; });
      this.grainientObserver.observe(this);
      this.grainientFrame = requestAnimationFrame(draw);
    } catch (error) {
      console.warn('Tooto protector Grainient background unavailable; using CSS fallback.', error);
    }
  }
}

if (!customElements.get('tooto-protector-features')) {
  customElements.define('tooto-protector-features', TootoProtectorFeatures);
}
